import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ReactTable from "components/lib/Global/ReactTable";
import { Constants } from "config/constants";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { getEmployeeAsync } from "state/slices/employee";
import OfficialSubRow from "components/CompanyDashboard/MaintainYourCompany/Review/OfficialSubRow";
import ApplicationCorrections from "components/CompanyDashboard/MaintainYourCompany/Corrections/ApplicationCorrections";
import SubRowAsync from "components/AccountSettings/SubRowAsync";
import { getFullName } from "utils";
import AvatarIcon from "components/lib/Shared/Icons/AvatarIcon";
import { SearchIcon } from "@heroicons/react/outline";

const CompanyOfficials = ({
  transactions,
  actionType,
  goToNextStep = () => null,
}) => {
  const isViewMode = false;
  const dispatch = useDispatch();
  const params = useParams();
  const [subActionType, setSubActionType] = useState(actionType);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState(0);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [workflowCompleted, setWorkflowCompleted] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [legalDepartmentEmployees, setLegalDepartmentEmployees] = useState([]);
  const [nonLegalDepartmentEmployees, setNonLegalDepartmentEmployees] =
    useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const currentUser = useSelector((state) => state.userSlice);
  const { dashboardRoles, designation } = useSelector(
    (state) => state.companyDashboardSlice
  );
  let filteredTransactions = transactions.filter((transaction) => {
    const searchFields = [
      transaction.changeType,
      // transaction.names?.firstName,
      // transaction.applicationType,
      // transaction.official?.idType,
    ];

    return searchFields.some((field) =>
      field ? field.toLowerCase().includes(searchTerm.toLowerCase()) : false
    );
  });

  const getCompanyOfficialsFromTransactions = (filteredTransactions) => {
    filteredTransactions = filteredTransactions.map((transaction) => {
      return transaction.officials.map((official, index) => {
        return {
          ...transaction,
          trxId: transaction._id,
          ...official,
          officialId: official._id,
          officialIndex: index,
        };
      });
    });
    let companyOfficialsData = [];
    filteredTransactions.map((trxArray) => {
      trxArray.forEach((data) => {
        companyOfficialsData.push(data);
      });
    });
    return companyOfficialsData;
  };
  const companyOfficialsData =
    getCompanyOfficialsFromTransactions(filteredTransactions);
  useEffect(() => {
    if (params.companyId) {
      dispatch(getEmployeeAsync({ companyId: params.companyId }));
    }
  }, [dispatch, params.companyId]);
  const { getEmployee } = useSelector((state) => state.employeeSlice);

  useEffect(() => {
    if (getEmployee?.items) {
      setLegalDepartmentEmployees(
        getEmployee.items.filter(
          (employee) => employee.isLegalDepartment === true
        )
      );
      setNonLegalDepartmentEmployees(
        getEmployee.items.filter(
          (employee) => employee.isLegalDepartment === false
        )
      );
    }
  }, [getEmployee]);

  const columns = [
    {
      Header: () => "Full Name",
      id: "Name",
      accessor: "fullName",
      Cell: ({ row: { original } }) => {
        const { names, fileUploads } = original;
        const fullName = getFullName(names) || "N/A";
        return (
          <div className="flex items-center space-x-1">
            <div className={`flex-shrink-0`}>
              {fileUploads?.passportPhoto ? (
                <img
                  className="w-8 h-8 rounded-full"
                  src={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${fileUploads?.passportPhoto}`}
                  alt={fullName}
                />
              ) : (
                <div className="w-8 h-8 rounded-full">
                  <div className="relative w-8 h-8 overflow-hidden bg-gray-100 rounded-full dark:bg-gray-600">
                    <AvatarIcon />
                  </div>
                </div>
              )}
            </div>
            <div className="px-3">
              <h1 className="">{fullName}</h1>
            </div>
          </div>
        );
      },
      // We can render something different for subRows
      SubCell: (cellProps) => <>{cellProps.value}</>,
    },
    {
      Header: "Change Type",
      accessor: "changeType",
      Cell: ({ row }) => {
        const capitalize = (str) => {
          if (!str) return null;
          return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };

        return (
          capitalize(row.original?.changeType) || row.original?.type || "-"
        );
      },
    },
    {
      Header: "ID Type",
      accessor: "idType",
      Cell: ({ row: { original } }) => {
        return original?.idType;
      },
    },
    {
      Header: "Designation",
      accessor: "designation",
      Cell: ({ row: { original } }) => {
        return original?.designation;
      },
    },
    {
      Header: "Action",
      Cell: ({ row }) => {
        React.useEffect(() => {
          if (row.index === 0 && !row.isExpanded && selectedRow === row.index) {
            row.toggleRowExpanded();
          }
        }, [row]);
        const handleToggle = () => {
          if (row.index === 0 && row.isExpanded) {
            row.toggleRowExpanded(false);
          } else {
            row.toggleRowExpanded();
          }
          setSelectedRow(row.index);
        };

        return (
          <button
            type="button"
            onClick={handleToggle}
            className="text-black rounded-md px-3 py-1 text-sm"
          >
            {row.isExpanded
              ? "Close"
              : actionType === "incorporation-view" ||
                actionType === "manage-transaction-view"
              ? "View"
              : actionType.charAt(0).toUpperCase() + actionType.slice(1)}
          </button>
        );
      },
    },
  ];

  const renderRowSubComponent = React.useCallback(
    ({ row, rowProps, visibleColumns }) => {
      if (actionType === "manage-transaction-view") {
        return (
          <OfficialSubRow
            row={row}
            rowProps={rowProps}
            visibleColumns={visibleColumns}
            currentTab={"stakeholder"}
            callFrom={"transaction"}
            isViewMode={true}
            isReviewer={false}
            isApprove={false}
            onComplete={(message) => {
              setWorkflowCompleted(true);
            }}
            onLastStepClose={() => {
              if (row.index < transactions.length - 1) {
                row.toggleRowExpanded(false);
                setSelectedRow(row.index + 1);
              }
            }}
          />
        );
      }
      if (actionType === "incorporation-view") {
        return (
          <SubRowAsync
            row={row}
            rowProps={rowProps}
            visibleColumns={visibleColumns}
            currentTab="stakeholderTab"
          />
        );
      }

      if (actionType === "corrections") {
        return (
          <ApplicationCorrections
            row={filteredTransactions[row.id]}
            rowProps={rowProps}
            visibleColumns={visibleColumns}
            setShowCorrectionModal={setShowCorrectionModal}
            currentTab="stakeholderTab"
            onComplete={(message) => {
              setWorkflowCompleted(true);
              setAlertMessage(message);
            }}
          />
        );
      }

      if (
        actionType === "review" ||
        actionType === "approve" ||
        actionType === "signature"
      ) {
        const isDirectorReviewOrApprove = dashboardRoles.some(
          (role) =>
            role.name ===
              Roles_Permissions_Constants.DASHBOARD_ROLES.REVIEWER &&
            [
              Constants.APPLICANT_CAPACITIES.DIRECTOR,
              Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER,
            ].includes(designation)
        );

        const isRegistrarReviewOrApprove = dashboardRoles.some(
          (role) =>
            role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
        );

        const isLegalDepartmentReviewOrApprove = legalDepartmentEmployees.some(
          (emp) => emp.user._id === currentUser._id
        );
        const isReviewer = (() => {
          if (
            isLegalDepartmentReviewOrApprove &&
            [Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_REVIEW].includes(
              row.original.status
            )
          ) {
            return true;
          }
          if (
            isDirectorReviewOrApprove &&
            [
              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_APPROVAL,
              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_APPROVAL,
              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_APPROVAL,
            ].includes(row.original.status)
          ) {
            return true;
          }
          if (isRegistrarReviewOrApprove) {
            return true;
          }
          return false;
        })();

        const isApprover = (() => {
          if (
            isLegalDepartmentReviewOrApprove &&
            [
              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_APPROVAL,
              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_REVIEW,
            ].includes(row.original.status)
          ) {
            return true;
          }
          if (
            isDirectorReviewOrApprove &&
            [
              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_REVIEW,
              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_REVIEW,
              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_APPROVAL,
            ].includes(row.original.status)
          ) {
            return true;
          }
          if (isRegistrarReviewOrApprove) {
            return true;
          }

          return false;
        })();

        return (
          <OfficialSubRow
            row={row}
            actionType={actionType}
            rowProps={rowProps}
            visibleColumns={visibleColumns}
            currentTab={"stakeholder"}
            callFrom={"transaction"}
            isViewMode={isViewMode}
            isReviewer={isReviewer}
            isApprove={isApprover}
            onComplete={(message) => {
              setWorkflowCompleted(true);
              setAlertMessage(message);
            }}
            onLastStepClose={() => {
              if (row.isExpanded) {
                row.toggleRowExpanded(false);
                setSelectedRow(row.index + 1);
              }
            }}
            goToNextStep={goToNextStep}
          />
        );
      }
    },
    [subActionType]
  );

  return (
    <>
      <div className="flex items-center justify-between mt-10">
        <div>
          <h2 className="font-semibold">Company Official</h2>
        </div>
        <div className="w-full max-w-lg lg:max-w-xs">
          <label htmlFor="search" className="sr-only">
            Search
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
              <SearchIcon
                className="w-5 h-5 text-gray-400"
                aria-hidden="true"
              />
            </div>
            <input
              id="search"
              name="search"
              className="block w-full py-2 pl-10 pr-3 leading-5 text-gray-300 placeholder-gray-400 border border-transparent rounded-md focus:outline-none focus:bg-white focus:text-gray-900 sm:text-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Search"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 w-full bg-white shadow overflow-x-auto">
        <ReactTable
          columns={columns}
          data={companyOfficialsData || []}
          dataRowCellStyle="pl-4 leading-snug py-2"
          renderRowSubComponent={renderRowSubComponent}
          paginate={true}
          totalRows={companyOfficialsData.length || 10}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          selectedRow={selectedRow}
        />
      </div>
    </>
  );
};

export default CompanyOfficials;
